import { useState, useEffect, useCallback, useRef } from 'react'
import {
  PageHeader,
  DataTable,
  Modal,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { relativeTime } from './fleet-helpers'
import { useTableColumns } from '../../hooks/use-table-columns'

interface AgentSuggestion {
  readonly id: string
  readonly agentTokenId: string
  readonly kind: 'log_pattern' | 'alert_rule' | 'upgrade' | 'config_change'
  readonly targetEnvironmentId: string | null
  readonly targetEnvironmentName: string | null
  readonly title: string
  readonly rationale: string
  readonly proposedChange: Record<string, unknown>
  readonly severity: 'info' | 'warn' | 'error'
  readonly status: 'pending' | 'accepted' | 'dismissed' | 'applied'
  readonly reviewedById: string | null
  readonly reviewedAt: string | null
  readonly appliedAt: string | null
  readonly createdAt: string
}

// Badge count: how many pending suggestions.
let pendingBadgeCount = 0
let badgeListeners: Array<(count: number) => void> = []

export function usePendingSuggestionsBadge(): number {
  const [count, setCount] = useState(pendingBadgeCount)
  useEffect(() => {
    badgeListeners.push(setCount)
    return () => {
      badgeListeners = badgeListeners.filter((l) => l !== setCount)
    }
  }, [])
  return count
}

function notifyBadge(count: number): void {
  pendingBadgeCount = count
  badgeListeners.forEach((l) => l(count))
}

const KIND_LABELS: Record<AgentSuggestion['kind'], string> = {
  log_pattern: 'Log Pattern',
  alert_rule: 'Alert Rule',
  upgrade: 'Upgrade',
  config_change: 'Config Change',
}

const SEV_COLORS: Record<AgentSuggestion['severity'], { bg: string; text: string }> = {
  info: { bg: '#dbeafe', text: '#1e40af' },
  warn: { bg: '#fef3c7', text: '#92400e' },
  error: { bg: '#fee2e2', text: '#991b1b' },
}

const STATUS_COLORS: Record<AgentSuggestion['status'], { bg: string; text: string }> = {
  pending: { bg: '#ede9fe', text: '#5b21b6' },
  accepted: { bg: '#d1fae5', text: '#065f46' },
  dismissed: { bg: '#f1f5f9', text: '#64748b' },
  applied: { bg: '#bbf7d0', text: '#14532d' },
}

function Pill({ bg, text, label }: { bg: string; text: string; label: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color: text,
        borderRadius: '9999px',
        padding: '2px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

type SuggestionFilter = {
  kind: string
  severity: string
  status: string
}

function DetailModal({
  suggestion,
  onClose,
  onTransition,
}: {
  suggestion: AgentSuggestion
  onClose: () => void
  onTransition: (id: string, status: 'accepted' | 'dismissed' | 'applied') => Promise<void>
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canAccept = suggestion.status === 'pending'
  const canDismiss = suggestion.status === 'pending' || suggestion.status === 'accepted'
  const canApply = suggestion.status === 'accepted'

  const doTransition = useCallback(
    async (status: 'accepted' | 'dismissed' | 'applied') => {
      setSaving(true)
      setError(null)
      try {
        await onTransition(suggestion.id, status)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to update status')
      } finally {
        setSaving(false)
      }
    },
    [suggestion.id, onTransition],
  )

  const sevColor = SEV_COLORS[suggestion.severity]
  const statusColor = STATUS_COLORS[suggestion.status]

  return (
    <Modal isOpen={true} onClose={onClose} title={suggestion.title} width="720px">
      <div style={{ padding: '1.25rem' }}>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              padding: '0.625rem 0.875rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Pill bg={sevColor.bg} text={sevColor.text} label={suggestion.severity.toUpperCase()} />
          <Pill bg={statusColor.bg} text={statusColor.text} label={suggestion.status} />
          <Pill bg="#f1f5f9" text="#475569" label={KIND_LABELS[suggestion.kind]} />
          {suggestion.targetEnvironmentName && (
            <Pill bg="#f0fdf4" text="#166534" label={suggestion.targetEnvironmentName} />
          )}
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '4px',
            }}
          >
            Rationale
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.6, color: colors.text }}>{suggestion.rationale}</p>
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '4px',
            }}
          >
            Proposed Change
          </div>
          <pre
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              padding: '0.75rem',
              borderRadius: '6px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '280px',
              overflowY: 'auto',
              margin: 0,
            }}
          >
            {JSON.stringify(suggestion.proposedChange, null, 2)}
          </pre>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '0.75rem',
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <button style={btnSecondary} onClick={onClose} disabled={saving}>
            Close
          </button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {canDismiss && (
              <button style={btnDanger} onClick={() => void doTransition('dismissed')} disabled={saving}>
                Dismiss
              </button>
            )}
            {canAccept && (
              <button style={btnPrimary} onClick={() => void doTransition('accepted')} disabled={saving}>
                Accept
              </button>
            )}
            {canApply && (
              <button
                style={{ ...btnPrimary, background: '#059669' }}
                onClick={() => void doTransition('applied')}
                disabled={saving}
              >
                Mark Applied
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export function AgentSuggestionsPage() {
  const [suggestions, setSuggestions] = useState<readonly AgentSuggestion[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AgentSuggestion | null>(null)
  const [filter, setFilter] = useState<SuggestionFilter>({ kind: '', severity: '', status: 'pending' })
  const [refreshKey, setRefreshKey] = useState(0)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('agent-suggestions')

  // Fetch suggestions list
  useEffect(() => {
    let cancelled = false
    const params = new URLSearchParams()
    if (filter.kind) params.set('kind', filter.kind)
    if (filter.severity) params.set('severity', filter.severity)
    if (filter.status) params.set('status', filter.status)
    params.set('limit', '100')

    api
      .get<readonly AgentSuggestion[]>(`/admin/agent-suggestions?${params.toString()}`)
      .then((res) => {
        if (cancelled) return
        setSuggestions(res.data ?? [])
        setTotal((res as unknown as { meta?: { total: number } }).meta?.total ?? 0)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load suggestions')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [filter, refreshKey])

  // Poll for pending badge count every 60s
  useEffect(() => {
    const poll = () => {
      api
        .get<{ count: number }>('/admin/agent-suggestions?status=pending&countOnly=1')
        .then((res) => notifyBadge(res.data?.count ?? 0))
        .catch(() => {})
      pollTimerRef.current = setTimeout(poll, 60_000)
    }
    poll()
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    }
  }, [])

  const handleTransition = useCallback(async (id: string, status: 'accepted' | 'dismissed' | 'applied') => {
    await api.patch<AgentSuggestion>(`/admin/agent-suggestions/${id}`, { status })
    setRefreshKey((k) => k + 1)
    setSelected(null)
  }, [])

  const setFilterKey = useCallback((key: keyof SuggestionFilter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: prev[key] === value ? '' : value }))
  }, [])

  const chipStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '0.8125rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: active ? `2px solid ${colors.primary}` : `2px solid ${colors.border}`,
    background: active ? '#eff6ff' : '#fff',
    color: active ? colors.primary : colors.text,
    userSelect: 'none',
  })

  const columns: readonly Column<AgentSuggestion>[] = [
    {
      key: 'createdAt',
      header: 'When',
      render: (r) => relativeTime(r.createdAt),
      sortable: true,
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (r) => KIND_LABELS[r.kind],
      sortable: true,
      filterable: true,
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (r) => {
        const c = SEV_COLORS[r.severity]
        return <Pill bg={c.bg} text={c.text} label={r.severity.toUpperCase()} />
      },
      sortable: true,
    },
    {
      key: 'targetEnvironmentName',
      header: 'Environment',
      render: (r) => r.targetEnvironmentName ?? '—',
      sortable: true,
    },
    {
      key: 'title',
      header: 'Title',
      render: (r) => <span style={{ fontSize: '0.875rem' }}>{r.title}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const c = STATUS_COLORS[r.status]
        return <Pill bg={c.bg} text={c.text} label={r.status} />
      },
      sortable: true,
    },
  ]

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title="Agent Suggestions"
        subtitle={`Write-once proposals submitted by LLM agents for human review${total > 0 ? ` — ${total.toLocaleString()} matching` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      {/* Filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {/* Status chips */}
        {(['pending', 'accepted', 'dismissed', 'applied'] as const).map((s) => (
          <span key={s} style={chipStyle(filter.status === s)} onClick={() => setFilterKey('status', s)}>
            {s}
          </span>
        ))}
        <span style={{ width: '1px', background: colors.border, margin: '0 4px' }} />
        {/* Kind chips */}
        {(['log_pattern', 'alert_rule', 'upgrade', 'config_change'] as const).map((k) => (
          <span key={k} style={chipStyle(filter.kind === k)} onClick={() => setFilterKey('kind', k)}>
            {KIND_LABELS[k]}
          </span>
        ))}
        <span style={{ width: '1px', background: colors.border, margin: '0 4px' }} />
        {/* Severity chips */}
        {(['info', 'warn', 'error'] as const).map((sev) => {
          const c = SEV_COLORS[sev]
          return (
            <span
              key={sev}
              style={{
                ...chipStyle(filter.severity === sev),
                ...(filter.severity === sev ? { background: c.bg, color: c.text, borderColor: c.text } : {}),
              }}
              onClick={() => setFilterKey('severity', sev)}
            >
              {sev.toUpperCase()}
            </span>
          )
        })}
      </div>

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#991b1b',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading…</div>
      ) : (
        <DataTable
          columns={columns}
          rows={suggestions as AgentSuggestion[]}
          keyField="id"
          emptyMessage="No suggestions match the current filters."
          onRowClick={(r) => setSelected(r)}
          {...tableColumnProps}
        />
      )}

      {selected && (
        <DetailModal suggestion={selected} onClose={() => setSelected(null)} onTransition={handleTransition} />
      )}
    </div>
  )
}
